import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { isEqual as _isEqual, keyBy as _keyBy } from 'lodash';
import { AppConfigStoreService } from 'src/app/shared/services';

// Named sets of Reordering columns, so the grid's many columns are picked once
// instead of adjusted by hand each time. Shared by everyone through the App
// Config Store; which one a browser is showing is kept in that browser.
export interface ColumnView {
    id: string;
    name: string;
    // dataFields. Order and width stay the grid's own, across views.
    columns: string[];
}

export interface ColumnViewsDoc {
    views: ColumnView[];
    // Switched to when ordering mode comes on, and left again when it goes off.
    orderingViewId: string | null;
}

const COLUMN_VIEWS_KEY = 'reordering-column-views';
// The view a browser shows outside ordering mode.
const ACTIVE_VIEW_STORAGE_KEY = 'reordering-active-view';

// Shown exactly while ordering mode is on, so no view gets a say in them.
export const ORDERING_ONLY_COLUMNS = ['order_qty', 'projected_total'];
// Shown in ordering mode whatever the view says: what is already on order is
// the first thing that decides a quantity. Views still choose it otherwise.
export const ORDERING_FORCED_COLUMNS = ['on_order_qty'];

export const DEFAULT_COLUMN_VIEWS: ColumnViewsDoc = {
    views: [
        {
            id: 'analysis',
            name: 'Analysis',
            columns: [
                'crawler_last_updated',
                'sku',
                'title',
                'class',
                'price.$numberDecimal',
                'total_sale',
                'demand',
                'remain',
                'stock_cover_days',
                'turnover',
                'rop',
                'eoq_units',
            ],
        },
        {
            id: 'ordering',
            name: 'Ordering',
            columns: [
                'sku',
                'title',
                'class',
                'price.$numberDecimal',
                'demand',
                'remain',
                'stock_cover_days',
                'on_order_qty',
                'rop',
                'eoq_units',
                'supplier.label',
                'supplier.shopName',
                'supplier.costCny',
                'supplier.lastOrderDate',
            ],
        },
    ],
    orderingViewId: 'ordering',
};

// The stored document is not validated, and a page with no view to show has
// nothing to fall back on, so anything unusable reads as the defaults.
export const normalizeColumnViews = (
    stored: Partial<ColumnViewsDoc> | null | undefined,
): ColumnViewsDoc => {
    const views = (stored?.views ?? []).filter(
        (view) =>
            view?.id &&
            typeof view.name === 'string' &&
            Array.isArray(view.columns),
    );
    if (views.length === 0) {
        return DEFAULT_COLUMN_VIEWS;
    }
    const orderingViewId = stored?.orderingViewId ?? null;
    return {
        views,
        orderingViewId: views.some((view) => view.id === orderingViewId)
            ? orderingViewId
            : null,
    };
};

export const isColumnVisible = (
    view: ColumnView,
    field: string,
    orderingMode: boolean,
): boolean =>
    ORDERING_ONLY_COLUMNS.includes(field)
        ? orderingMode
        : view.columns.includes(field) ||
          (orderingMode && ORDERING_FORCED_COLUMNS.includes(field));

// What someone changed since `base`, laid over what is stored now. The popup
// edits a copy that may be old and the store replaces whole documents, so
// writing that copy back would erase the views others saved in the meantime.
export const mergeColumnViews = (
    base: ColumnViewsDoc,
    draft: ColumnViewsDoc,
    latest: ColumnViewsDoc,
): ColumnViewsDoc => {
    const baseById = _keyBy(base.views, 'id');
    const draftById = _keyBy(draft.views, 'id');
    const latestById = _keyBy(latest.views, 'id');
    const edited = draft.views.filter(
        (view) => !_isEqual(view, baseById[view.id]),
    );
    const editedById = _keyBy(edited, 'id');
    const views = [
        ...latest.views
            .filter((view) => draftById[view.id] || !baseById[view.id])
            .map((view) => editedById[view.id] ?? view),
        ...edited.filter((view) => !latestById[view.id]),
    ];
    const orderingViewId =
        draft.orderingViewId !== base.orderingViewId
            ? draft.orderingViewId
            : latest.orderingViewId;
    return {
        views,
        orderingViewId: views.some((view) => view.id === orderingViewId)
            ? orderingViewId
            : null,
    };
};

// Blocked or full storage costs remembering the view, nothing else.
const readStoredViewId = (): string | null => {
    try {
        return localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY);
    } catch {
        return null;
    }
};

const storeViewId = (id: string): void => {
    try {
        localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, id);
    } catch (err) {
        console.error(err);
    }
};

// Which view is showing, and what ordering mode does to that. The grid is the
// component's business; this only decides.
export class ColumnViewSelection {
    doc = DEFAULT_COLUMN_VIEWS;
    activeId = readStoredViewId() ?? DEFAULT_COLUMN_VIEWS.views[0].id;
    // Set exactly while ordering mode is on: what to go back to when it ends.
    private idBeforeOrdering: string | null = null;
    private pickedInOrdering = false;

    get active(): ColumnView {
        return (
            this.doc.views.find((view) => view.id === this.activeId) ??
            this.doc.views[0]
        );
    }

    // In ordering mode only a view the mode chose follows the document. One
    // someone picked stands, including through saving the views themselves.
    use(doc: ColumnViewsDoc): void {
        this.doc = doc;
        if (
            this.idBeforeOrdering !== null &&
            !this.pickedInOrdering &&
            doc.orderingViewId
        ) {
            this.activeId = doc.orderingViewId;
        }
        this.settle();
    }

    // A pick made in ordering mode lasts until the mode ends, so it doesn't
    // change which view the browser comes back to.
    pick(id: string): void {
        this.activeId = id;
        if (this.idBeforeOrdering === null) {
            storeViewId(id);
        } else {
            this.pickedInOrdering = true;
        }
    }

    enterOrdering(): void {
        this.idBeforeOrdering = this.activeId;
        this.pickedInOrdering = false;
        this.activeId = this.doc.orderingViewId ?? this.activeId;
    }

    leaveOrdering(): void {
        this.activeId = this.idBeforeOrdering ?? this.activeId;
        this.idBeforeOrdering = null;
        this.settle();
    }

    // The active view may be one that has since been deleted.
    private settle(): void {
        const id = this.active.id;
        if (id !== this.activeId) {
            this.activeId = id;
            if (this.idBeforeOrdering === null) {
                storeViewId(id);
            }
        }
    }
}

export const newColumnViewId = (): string =>
    `view-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

@Injectable({
    providedIn: 'root',
})
export class ColumnViewsService {
    private configStore = inject(AppConfigStoreService);

    get(): Observable<ColumnViewsDoc> {
        return this.configStore
            .get<Partial<ColumnViewsDoc>>(COLUMN_VIEWS_KEY)
            .pipe(map((stored) => normalizeColumnViews(stored)));
    }

    // Answers with what was stored, which may hold views saved by others.
    save(
        base: ColumnViewsDoc,
        draft: ColumnViewsDoc,
    ): Observable<ColumnViewsDoc> {
        return this.get().pipe(
            switchMap((latest) => {
                const merged = mergeColumnViews(base, draft, latest);
                return this.configStore
                    .set(COLUMN_VIEWS_KEY, merged)
                    .pipe(map(() => merged));
            }),
        );
    }
}
